import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Download, Lock } from 'lucide-react'
import { ColumnDef, ColumnFiltersState } from '@tanstack/react-table'

import XLSX from 'xlsx-js-style'
import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import VirtualTable from '../components/VirtualTable'

// -- Types --

interface TripDetail {
  id: string
  bill_uuid: string | null
  booking_uuid: string | null
  user_uuid: string | null
  bill_datetime: string | null
  is_combination: boolean | null
  name_first: string | null
  name_last: string | null
  additional_info: string | null
  account_type_name: string | null
  address: string | null
  distance_meters: number | null
  copayment: number | null
  customer_share: number | null
  cost_center: string | null
  items: Record<string, unknown> | null
  correction_comments: string | null
}

interface InvoiceLine {
  id: string
  line_number: number
  description: string | null
  cost_center_code: string | null
  cost_center_name: string | null
  amount_net: number | null
  vat_rate: number | null
  row_type: string | null
  trip_count: number | null
  has_inspection_access: boolean
  trip_details: TripDetail[]
}

export interface InvoiceInspection {
  id: string
  invoice_number: string
  invoice_date: string
  period_start: string | null
  period_end: string | null
  net_amount: number | null
  trip_count: number
  status: string
  billing_group: string | null
  lines: InvoiceLine[]
}

// -- Item type pivot columns --

const ITEM_TYPES = [
  { id: 1, tKey: 'startFee' },
  { id: 3, tKey: 'wait' },
  { id: 8, tKey: 'waypoint' },
  { id: 4, tKey: 'routeDrive' },
  { id: 5, tKey: 'assistance' },
  { id: 6, tKey: 'stairclimber' },
  { id: 16, tKey: 'escort' },
  { id: 19, tKey: 'stretcher' },
] as const

// -- Helpers --

function formatDate(isoStr: string | null): string {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  return d.toLocaleDateString('fi-FI')
}

function formatDateTime(isoStr: string | null): string {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  return `${d.toLocaleDateString('fi-FI')} ${d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}`
}

function formatCurrency(val: number | string | null): string {
  if (val === null || val === undefined || val === '') return ''
  return Number(val).toLocaleString('fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function shortUuid(uuid: string | null): string {
  if (!uuid) return ''
  return uuid.split('-')[0] || uuid.substring(0, 8)
}

/** Excel-safe, unique worksheet name (max 31 chars, no []:*?/\). */
function safeSheetName(name: string, used: Set<string>): string {
  let clean = (name || 'Sheet').replace(/[[\]:*?/\\]/g, '-').trim().substring(0, 31)
  if (!clean) clean = 'Sheet'
  const base = clean
  let i = 1
  while (used.has(clean.toLowerCase())) {
    const suffix = `~${i}`
    clean = base.substring(0, 31 - suffix.length) + suffix
    i += 1
  }
  used.add(clean.toLowerCase())
  return clean
}

// -- Component --

interface InvoiceInspectionPageProps {
  /** Pre-loaded data (from access code lookup) */
  data?: InvoiceInspection
  isLoading?: boolean
  error?: Error | null
  /** True when accessed via access code (changes back link) */
  isAccessCodeView?: boolean
}

export default function InvoiceInspectionPage({
  data: preloadedData,
  isLoading: preloadedLoading,
  error: preloadedError,
  isAccessCodeView,
}: InvoiceInspectionPageProps = {}) {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const { t: tb } = useTranslation('billing')

  // Only fetch if not pre-loaded (i.e., navigated via search, not access code)
  const {
    data: fetchedData,
    isLoading: fetchedLoading,
    error: fetchedError,
  } = useQuery<InvoiceInspection>({
    queryKey: ['invoiceInspection', id],
    queryFn: () => apiClient.get(`/invoices/${id}/inspection`),
    enabled: !!id && !preloadedData,
  })

  const invoice = preloadedData || fetchedData
  const isLoading = preloadedLoading || fetchedLoading
  const error = preloadedError || fetchedError

  // Trip detail columns for VirtualTable
  const tripDetailColumns = useMemo<ColumnDef<TripDetail, unknown>[]>(
    () => [
      {
        id: 'bill_datetime',
        accessorKey: 'bill_datetime',
        header: t('inspection.billDatetime'),
        cell: ({ getValue }) => formatDateTime(getValue() as string | null),
        filterFn: 'includesString',
        size: 120,
      },
      {
        id: 'bill_uuid',
        accessorKey: 'bill_uuid',
        header: t('inspection.billUuid'),
        cell: ({ getValue }) => shortUuid(getValue() as string | null),
        filterFn: 'includesString',
        size: 80,
      },
      {
        id: 'booking_uuid',
        accessorKey: 'booking_uuid',
        header: t('inspection.bookingUuid'),
        cell: ({ getValue }) => shortUuid(getValue() as string | null),
        filterFn: 'includesString',
        size: 80,
      },
      {
        id: 'name_last',
        accessorKey: 'name_last',
        header: t('inspection.nameLast'),
        filterFn: 'includesString',
        size: 100,
      },
      {
        id: 'name_first',
        accessorKey: 'name_first',
        header: t('inspection.nameFirst'),
        filterFn: 'includesString',
        size: 100,
      },
      {
        id: 'account_type_name',
        accessorKey: 'account_type_name',
        header: t('inspection.accountTypeName'),
        filterFn: 'includesString',
        size: 100,
      },
      {
        id: 'address',
        accessorKey: 'address',
        header: t('inspection.address'),
        filterFn: 'includesString',
        size: 200,
      },
      {
        id: 'distance_km',
        accessorFn: (row: TripDetail) => {
          if (row.distance_meters == null) return null
          return (row.distance_meters / 1000).toFixed(1)
        },
        header: t('inspection.distanceKm'),
        filterFn: 'includesString',
        size: 60,
        meta: { align: 'right' },
      },
      {
        id: 'total',
        accessorFn: (row: TripDetail) => {
          const items = row.items as unknown as { type_id: number; amount_net: string }[] | null
          if (!items || items.length === 0) return null
          return items.reduce((sum, it) => sum + Number(it.amount_net || 0), 0)
        },
        header: t('inspection.total'),
        cell: ({ getValue }: { getValue: () => unknown }) =>
          formatCurrency(getValue() as number | null),
        filterFn: 'includesString',
        size: 80,
        meta: { align: 'right' },
      },
      {
        id: 'customer_share',
        accessorKey: 'customer_share',
        header: t('inspection.customerShare'),
        cell: ({ getValue }) => formatCurrency(getValue() as number | null),
        filterFn: 'includesString',
        size: 80,
        meta: { align: 'right' },
      },
      {
        id: 'copayment',
        accessorKey: 'copayment',
        header: t('inspection.copayment'),
        cell: ({ getValue }) => formatCurrency(getValue() as number | null),
        filterFn: 'includesString',
        size: 80,
        meta: { align: 'right' },
      },
      // Pivot columns from items JSONB
      ...ITEM_TYPES.map((it) => ({
        id: `item_${it.id}`,
        accessorFn: (row: TripDetail) => {
          const items = row.items as unknown as { type_id: number; amount_net: string }[] | null
          const item = items?.find((i: { type_id: number }) => i.type_id === it.id)
          return item ? item.amount_net : null
        },
        header: tb(`inspection.items.${it.tKey}`, { defaultValue: it.tKey }),
        cell: ({ getValue }: { getValue: () => unknown }) =>
          formatCurrency(getValue() as string | null),
        filterFn: 'includesString' as const,
        size: 70,
        meta: { align: 'right' },
      })),
      {
        id: 'is_combination',
        accessorFn: (row: TripDetail) => (row.is_combination ? 'K' : ''),
        header: t('inspection.isCombination'),
        filterFn: 'includesString',
        size: 50,
      },
      {
        id: 'additional_info',
        accessorKey: 'additional_info',
        header: t('inspection.additionalInfo'),
        filterFn: 'includesString',
        size: 120,
      },
      {
        id: 'correction_comments',
        accessorKey: 'correction_comments',
        header: t('inspection.correctionComments'),
        filterFn: 'includesString',
        size: 200,
      },
    ],
    [t, tb]
  )

  // Column ID → header label map for filter display
  const columnHeaderMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {
      bill_datetime: t('inspection.billDatetime'),
      bill_uuid: t('inspection.billUuid'),
      booking_uuid: t('inspection.bookingUuid'),
      name_last: t('inspection.nameLast'),
      name_first: t('inspection.nameFirst'),
      account_type_name: t('inspection.accountTypeName'),
      address: t('inspection.address'),
      distance_km: t('inspection.distanceKm'),
      total: t('inspection.total'),
      customer_share: t('inspection.customerShare'),
      copayment: t('inspection.copayment'),
      is_combination: t('inspection.isCombination'),
      additional_info: t('inspection.additionalInfo'),
    }
    for (const it of ITEM_TYPES) {
      map[`item_${it.id}`] = tb(`inspection.items.${it.tKey}`, { defaultValue: it.tKey })
    }
    return map
  }, [t, tb])

  // Excel export for a single invoice line
  const handleExportLine = useCallback(
    (
      line: InvoiceLine,
      filteredData: TripDetail[],
      filterInfo?: { globalFilter: string; columnFilters: ColumnFiltersState },
    ) => {
      if (!filteredData.length) return

      // -- Styles --
      const metaStyle = { font: { bold: true, sz: 11 } }
      const filterStyle = { font: { italic: true, sz: 10, color: { rgb: '666666' } } }
      const headerStyle = {
        font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '4472C4' } },
        border: {
          bottom: { style: 'thin', color: { rgb: '2F5496' } },
        },
      }
      const evenRowStyle = {
        fill: { fgColor: { rgb: 'F2F2F2' } },
      }

      const rows: unknown[][] = []

      // Row 0: line metadata (no euro amount)
      rows.push([
        `${t('inspection.lineNumber')} ${line.line_number}`,
        line.description || '',
        line.cost_center_code
          ? `${line.cost_center_code} ${line.cost_center_name || ''}`
          : '',
      ])

      // Row 1: empty separator
      rows.push([])

      // Row 2: filter info (if any filters active)
      const filterParts: string[] = []
      if (filterInfo?.globalFilter) {
        filterParts.push(`Haku: "${filterInfo.globalFilter}"`)
      }
      if (filterInfo?.columnFilters?.length) {
        for (const cf of filterInfo.columnFilters) {
          const label = columnHeaderMap[cf.id] || cf.id
          filterParts.push(`${label}: "${cf.value}"`)
        }
      }
      if (filterParts.length > 0) {
        rows.push([`Suodattimet: ${filterParts.join(', ')}`])
        // Row 3: empty separator after filter info
        rows.push([])
      }

      // Header row index (depends on whether filter row was added)
      const headerRowIdx = rows.length

      // Column headers
      const headers = [
        t('inspection.billDatetime'),
        t('inspection.billUuid'),
        t('inspection.bookingUuid'),
        t('inspection.nameLast'),
        t('inspection.nameFirst'),
        t('inspection.accountTypeName'),
        t('inspection.address'),
        t('inspection.distanceKm'),
        t('inspection.total'),
        t('inspection.customerShare'),
        t('inspection.copayment'),
        ...ITEM_TYPES.map((it) =>
          tb(`inspection.items.${it.tKey}`, { defaultValue: it.tKey })
        ),
        t('inspection.isCombination'),
        t('inspection.additionalInfo'),
      ]
      rows.push(headers)

      // Data rows
      const dataRows = filteredData.map((td) => {
        const items = td.items as unknown as
          | { type_id: number; amount_net: string }[]
          | null
        const total = items
          ? items.reduce((sum, it) => sum + Number(it.amount_net || 0), 0)
          : null

        return [
          td.bill_datetime ? formatDateTime(td.bill_datetime) : '',
          td.bill_uuid || '',
          td.booking_uuid || '',
          td.name_last || '',
          td.name_first || '',
          td.account_type_name || '',
          td.address || '',
          td.distance_meters != null
            ? Number((td.distance_meters / 1000).toFixed(1))
            : '',
          total != null ? Number(total.toFixed(2)) : '',
          td.customer_share != null ? Number(td.customer_share) : '',
          td.copayment != null ? Number(td.copayment) : '',
          ...ITEM_TYPES.map((it) => {
            const item = items?.find(
              (i: { type_id: number }) => i.type_id === it.id
            )
            return item ? Number(item.amount_net) : ''
          }),
          td.is_combination ? 'K' : '',
          td.additional_info || '',
        ]
      })
      rows.push(...dataRows)

      const ws = XLSX.utils.aoa_to_sheet(rows)

      // -- Apply cell styles --
      const colCount = headers.length

      // Meta row (row 0): bold
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c })
        if (ws[addr]) ws[addr].s = metaStyle
      }

      // Filter row style (if present)
      if (filterParts.length > 0) {
        const addr = XLSX.utils.encode_cell({ r: 2, c: 0 })
        if (ws[addr]) ws[addr].s = filterStyle
      }

      // Header row: bold white on blue
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r: headerRowIdx, c })
        if (ws[addr]) ws[addr].s = headerStyle
      }

      // Data rows: alternating gray background on even rows
      for (let i = 0; i < dataRows.length; i++) {
        if (i % 2 === 1) continue // odd rows keep default (white)
        const rowIdx = headerRowIdx + 1 + i
        for (let c = 0; c < colCount; c++) {
          const addr = XLSX.utils.encode_cell({ r: rowIdx, c })
          if (ws[addr]) {
            ws[addr].s = evenRowStyle
          } else {
            ws[addr] = { v: '', t: 's', s: evenRowStyle }
          }
        }
      }

      // Column widths
      ws['!cols'] = [
        { wch: 16 }, // bill_datetime
        { wch: 38 }, // bill_uuid
        { wch: 38 }, // booking_uuid
        { wch: 14 }, // name_last
        { wch: 14 }, // name_first
        { wch: 14 }, // account_type_name
        { wch: 30 }, // address
        { wch: 8 },  // distance_km
        { wch: 10 }, // total
        { wch: 10 }, // customer_share
        { wch: 10 }, // copayment
        ...ITEM_TYPES.map(() => ({ wch: 10 })),
        { wch: 8 },  // is_combination
        { wch: 20 }, // additional_info
      ]

      // AutoFilter on header row
      ws['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: headerRowIdx, c: 0 },
          e: { r: headerRowIdx + dataRows.length, c: colCount - 1 },
        }),
      }

      // Freeze panes: header row stays visible when scrolling
      ws['!freeze'] = { xSplit: 0, ySplit: headerRowIdx + 1 }

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Tarkastus')

      const ccPart = line.cost_center_code
        ? ` - ${line.cost_center_code} ${line.cost_center_name || ''}`
        : ''
      const safeName = `Laskun tarkastus - Rivi ${line.line_number}${ccPart}`
        .replace(/[/\\:*?"<>|]/g, '_')
      XLSX.writeFile(wb, `${safeName}.xlsx`)

      // Log export to audit trail
      apiClient
        .post('/audit', {
          action: 'excel_export',
          resource_type: 'invoice_inspection',
          resource_id: invoice?.id,
          details: {
            invoice_number: invoice?.invoice_number,
            line_number: line.line_number,
            cost_center_code: line.cost_center_code,
            row_count: filteredData.length,
            filename: `${safeName}.xlsx`,
          },
        })
        .catch(() => {
          /* audit log failure should not block user */
        })
    },
    [t, tb, columnHeaderMap, invoice]
  )

  // Excel export: whole invoice combined into a single workbook
  // (Summary + line list + all trips + credits + one sheet per cost center)
  const handleExportAll = useCallback(() => {
    if (!invoice) return
    const lines = invoice.lines

    // -- Styles --
    const headerStyle = {
      font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '4472C4' } },
      border: { bottom: { style: 'thin', color: { rgb: '2F5496' } } },
    }
    const evenRowStyle = { fill: { fgColor: { rgb: 'F2F2F2' } } }
    const titleStyle = { font: { bold: true, sz: 13 } }
    const labelStyle = { font: { bold: true, sz: 10 } }
    const totalStyle = { font: { bold: true, sz: 10 } }

    // -- Detail sheet columns (shared by all trip/credit sheets) --
    const detailHeaders = [
      t('inspection.lineNumber'),
      t('inspection.costCenter'),
      t('inspection.colType'),
      t('inspection.billDatetime'),
      t('inspection.billUuid'),
      t('inspection.bookingUuid'),
      t('inspection.nameLast'),
      t('inspection.nameFirst'),
      t('inspection.accountTypeName'),
      t('inspection.address'),
      t('inspection.distanceKm'),
      t('inspection.total'),
      t('inspection.customerShare'),
      t('inspection.copayment'),
      ...ITEM_TYPES.map((it) => tb(`inspection.items.${it.tKey}`, { defaultValue: it.tKey })),
      t('inspection.isCombination'),
      t('inspection.additionalInfo'),
      t('inspection.correctionComments'),
    ]
    const detailColCount = detailHeaders.length
    const detailWidths = [
      { wch: 6 }, { wch: 26 }, { wch: 10 }, { wch: 16 }, { wch: 12 }, { wch: 12 },
      { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 34 }, { wch: 7 }, { wch: 9 },
      { wch: 9 }, { wch: 10 },
      ...ITEM_TYPES.map(() => ({ wch: 11 })),
      { wch: 10 }, { wch: 20 }, { wch: 40 },
    ]

    const typeLabel = (rt: string | null): string =>
      rt === 'credit'
        ? t('inspection.lineCredit')
        : rt === 'correction'
          ? t('inspection.lineCorrection')
          : rt === 'service'
            ? t('inspection.lineService')
            : t('inspection.lineTrip')

    const ccLabel = (line: InvoiceLine): string =>
      line.cost_center_code
        ? `${line.cost_center_code}${line.cost_center_name ? ` ${line.cost_center_name}` : ''}`
        : ''

    const buildDetailRow = (line: InvoiceLine, td: TripDetail): unknown[] => {
      const items = td.items as unknown as { type_id: number; amount_net: string }[] | null
      const total = items ? items.reduce((s, it) => s + Number(it.amount_net || 0), 0) : null
      return [
        line.line_number,
        ccLabel(line),
        typeLabel(line.row_type),
        td.bill_datetime ? formatDateTime(td.bill_datetime) : '',
        td.bill_uuid || '',
        td.booking_uuid || '',
        td.name_last || '',
        td.name_first || '',
        td.account_type_name || '',
        td.address || '',
        td.distance_meters != null ? Number((td.distance_meters / 1000).toFixed(1)) : '',
        total != null ? Number(total.toFixed(2)) : '',
        td.customer_share != null ? Number(td.customer_share) : '',
        td.copayment != null ? Number(td.copayment) : '',
        ...ITEM_TYPES.map((it) => {
          const item = items?.find((i) => i.type_id === it.id)
          return item ? Number(item.amount_net) : ''
        }),
        td.is_combination ? 'K' : '',
        td.additional_info || '',
        td.correction_comments || '',
      ]
    }

    const styleDetailSheet = (ws: XLSX.WorkSheet, nRows: number) => {
      for (let c = 0; c < detailColCount; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c })
        if (ws[addr]) ws[addr].s = headerStyle
      }
      for (let i = 0; i < nRows; i++) {
        if (i % 2 === 1) continue
        const r = 1 + i
        for (let c = 0; c < detailColCount; c++) {
          const addr = XLSX.utils.encode_cell({ r, c })
          if (ws[addr]) ws[addr].s = evenRowStyle
          else ws[addr] = { v: '', t: 's', s: evenRowStyle }
        }
      }
      ws['!cols'] = detailWidths
      ws['!freeze'] = { xSplit: 0, ySplit: 1 }
      ws['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: nRows, c: detailColCount - 1 },
        }),
      }
    }

    const wb = XLSX.utils.book_new()
    const usedNames = new Set<string>()

    // -- Sheet 1: Summary (invoice info + per-cost-center totals) --
    const strip = (s: string | null) => (s && s.startsWith('B-') ? s.substring(2) : s || '')
    const summaryRows: unknown[][] = [
      [t('inspection.summaryTitle')],
      [],
      [t('inspection.billingGroup'), invoice.billing_group || ''],
      [t('inspection.invoiceCode'), strip(invoice.invoice_number)],
      [t('inspection.invoiceDate'), formatDate(invoice.invoice_date)],
      [
        t('inspection.period'),
        invoice.period_start && invoice.period_end
          ? `${formatDate(invoice.period_start)} – ${formatDate(invoice.period_end)}`
          : '',
      ],
      [t('inspection.netAmount'), invoice.net_amount != null ? Number(invoice.net_amount) : ''],
      [t('inspection.tripCount'), invoice.trip_count],
      [],
    ]
    // Per-cost-center aggregation
    interface CcAgg {
      code: string
      name: string
      trip: number
      service: number
      credit: number
      net: number
      trips: number
    }
    const ccOrder: string[] = []
    const ccMap: Record<string, CcAgg> = {}
    for (const ln of lines) {
      const key = ln.cost_center_code || '(–)'
      if (!ccMap[key]) {
        ccMap[key] = {
          code: key,
          name: ln.cost_center_name || '',
          trip: 0,
          service: 0,
          credit: 0,
          net: 0,
          trips: 0,
        }
        ccOrder.push(key)
      }
      const agg = ccMap[key]
      const amt = Number(ln.amount_net || 0)
      agg.net += amt
      if (ln.row_type === 'trip') {
        agg.trip += amt
        agg.trips += ln.trip_count || 0
      } else if (ln.row_type === 'service') {
        agg.service += amt
      } else {
        agg.credit += amt
      }
    }
    const sumHeaderRowIdx = summaryRows.length
    summaryRows.push([
      t('inspection.costCenter'),
      t('inspection.colName'),
      t('inspection.sumTrips'),
      t('inspection.sumService'),
      t('inspection.sumCredits'),
      t('inspection.sumNet'),
      t('inspection.tripCount'),
    ])
    const grand: CcAgg = { code: '', name: '', trip: 0, service: 0, credit: 0, net: 0, trips: 0 }
    for (const key of ccOrder) {
      const a = ccMap[key]
      summaryRows.push([
        a.code,
        a.name,
        Number(a.trip.toFixed(2)),
        Number(a.service.toFixed(2)),
        Number(a.credit.toFixed(2)),
        Number(a.net.toFixed(2)),
        a.trips,
      ])
      grand.trip += a.trip
      grand.service += a.service
      grand.credit += a.credit
      grand.net += a.net
      grand.trips += a.trips
    }
    const totalRowIdx = summaryRows.length
    summaryRows.push([
      t('inspection.grandTotal'),
      '',
      Number(grand.trip.toFixed(2)),
      Number(grand.service.toFixed(2)),
      Number(grand.credit.toFixed(2)),
      Number(grand.net.toFixed(2)),
      grand.trips,
    ])

    const summaryWs = XLSX.utils.aoa_to_sheet(summaryRows)
    summaryWs['!cols'] = [
      { wch: 18 }, { wch: 30 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 9 },
    ]
    if (summaryWs['A1']) summaryWs['A1'].s = titleStyle
    for (let r = 2; r <= 7; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: 0 })
      if (summaryWs[addr]) summaryWs[addr].s = labelStyle
    }
    for (let c = 0; c < 7; c++) {
      const addr = XLSX.utils.encode_cell({ r: sumHeaderRowIdx, c })
      if (summaryWs[addr]) summaryWs[addr].s = headerStyle
      const taddr = XLSX.utils.encode_cell({ r: totalRowIdx, c })
      if (summaryWs[taddr]) summaryWs[taddr].s = totalStyle
    }
    XLSX.utils.book_append_sheet(
      wb,
      summaryWs,
      safeSheetName(t('inspection.sheetSummary'), usedNames),
    )

    // -- Sheet 2: All invoice lines --
    const lineHeaders = [
      t('inspection.lineNumber'),
      t('inspection.costCenter'),
      t('inspection.colName'),
      t('inspection.colDescription'),
      t('inspection.colType'),
      t('inspection.sumNet'),
      t('inspection.tripCount'),
      t('inspection.correctionComments'),
    ]
    const lineCommentByNumber: Record<number, string> = {}
    for (const ln of lines) {
      for (const td of ln.trip_details) {
        if (td.correction_comments && lineCommentByNumber[ln.line_number] == null) {
          lineCommentByNumber[ln.line_number] = td.correction_comments
        }
      }
    }
    const lineRows: unknown[][] = [lineHeaders]
    for (const ln of lines) {
      lineRows.push([
        ln.line_number,
        ln.cost_center_code || '',
        ln.cost_center_name || '',
        ln.description || '',
        typeLabel(ln.row_type),
        ln.amount_net != null ? Number(ln.amount_net) : '',
        ln.trip_count || 0,
        lineCommentByNumber[ln.line_number] || '',
      ])
    }
    const linesWs = XLSX.utils.aoa_to_sheet(lineRows)
    linesWs['!cols'] = [
      { wch: 6 }, { wch: 12 }, { wch: 26 }, { wch: 44 }, { wch: 14 }, { wch: 12 }, { wch: 9 }, { wch: 44 },
    ]
    for (let c = 0; c < lineHeaders.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c })
      if (linesWs[addr]) linesWs[addr].s = headerStyle
    }
    for (let i = 1; i < lineRows.length; i++) {
      if (i % 2 === 0) continue
      for (let c = 0; c < lineHeaders.length; c++) {
        const addr = XLSX.utils.encode_cell({ r: i, c })
        if (linesWs[addr]) linesWs[addr].s = evenRowStyle
        else linesWs[addr] = { v: '', t: 's', s: evenRowStyle }
      }
    }
    linesWs['!freeze'] = { xSplit: 0, ySplit: 1 }
    linesWs['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: lineRows.length - 1, c: lineHeaders.length - 1 },
      }),
    }
    XLSX.utils.book_append_sheet(wb, linesWs, safeSheetName(t('inspection.sheetLines'), usedNames))

    // -- Sheet 3: All trips / Sheet 4: Credits --
    const makeDetailSheet = (title: string, srcLines: InvoiceLine[]) => {
      const rows: unknown[][] = [detailHeaders]
      let n = 0
      for (const line of srcLines) {
        for (const td of line.trip_details) {
          rows.push(buildDetailRow(line, td))
          n += 1
        }
      }
      const ws = XLSX.utils.aoa_to_sheet(rows)
      styleDetailSheet(ws, n)
      XLSX.utils.book_append_sheet(wb, ws, safeSheetName(title, usedNames))
      return n
    }

    const tripLines = lines.filter((l) => l.row_type === 'trip')
    const creditLines = lines.filter(
      (l) => l.row_type === 'credit' || l.row_type === 'correction',
    )
    makeDetailSheet(t('inspection.sheetAllTrips'), tripLines)
    const creditCount = creditLines.reduce((s, l) => s + l.trip_details.length, 0)
    if (creditCount > 0) {
      makeDetailSheet(t('inspection.sheetCredits'), creditLines)
    }

    // -- Sheets 5..N: one per cost center (trips + credits for that CC) --
    const byCc: Record<string, { name: string; lines: InvoiceLine[] }> = {}
    const byCcOrder: string[] = []
    for (const line of lines) {
      if (!line.trip_details.length) continue
      const key = line.cost_center_code || '(–)'
      if (!byCc[key]) {
        byCc[key] = { name: line.cost_center_name || '', lines: [] }
        byCcOrder.push(key)
      }
      byCc[key].lines.push(line)
    }
    byCcOrder.sort()
    for (const key of byCcOrder) {
      const info = byCc[key]
      makeDetailSheet(`${key} ${info.name}`.trim(), info.lines)
    }

    // -- File name + save --
    const groupPart = invoice.billing_group
      ? invoice.billing_group
      : invoice.period_start && invoice.period_end
        ? `${formatDate(invoice.period_start)}-${formatDate(invoice.period_end)}`
        : invoice.invoice_number
    const safeFile = `${t('inspection.summaryTitle')} - ${groupPart}`.replace(
      /[/\\:*?"<>|]/g,
      '_',
    )
    XLSX.writeFile(wb, `${safeFile}.xlsx`)

    // Audit log
    apiClient
      .post('/audit', {
        action: 'excel_export',
        resource_type: 'invoice_inspection',
        resource_id: invoice.id,
        details: {
          invoice_number: invoice.invoice_number,
          scope: 'all_lines_combined',
          line_count: lines.length,
          filename: `${safeFile}.xlsx`,
        },
      })
      .catch(() => {
        /* audit log failure should not block user */
      })
  }, [t, tb, invoice])

  // Stats: unique trips, orders, customers
  const renderInspectionStats = useCallback(
    (filteredData: TripDetail[]) => {
      const bills = new Set<string>()
      const bookings = new Set<string>()
      const users = new Set<string>()
      for (const td of filteredData) {
        if (td.bill_uuid) bills.add(td.bill_uuid)
        if (td.booking_uuid) bookings.add(td.booking_uuid)
        if (td.user_uuid) users.add(td.user_uuid)
      }
      return (
        <>
          <span className="border-l pl-3">{bills.size} {t('inspection.uniqueBills')}</span>
          <span>{bookings.size} {t('inspection.uniqueBookings')}</span>
          <span>{users.size} {t('inspection.uniqueUsers')}</span>
        </>
      )
    },
    [t]
  )

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error || !invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{tc('errors.generic')}</p>
        <Link
          to="/invoices"
          className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('inspection.backToInvoices')}
        </Link>
      </div>
    )
  }

  const backTo = isAccessCodeView ? '/' : '/invoices'
  const backLabel = isAccessCodeView
    ? tc('dashboard.title')
    : t('inspection.backToInvoices')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to={backTo}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {backLabel}
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('inspection.title')}
          {invoice.billing_group
            ? ` — ${invoice.billing_group}`
            : invoice.period_start && invoice.period_end
              ? ` — ${formatDate(invoice.period_start)} \u2013 ${formatDate(invoice.period_end)}`
              : ''}
        </h1>
      </div>

      {/* Invoice Info Card */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('inspection.invoiceInfo')}
          </h2>
          <button
            onClick={handleExportAll}
            disabled={!invoice.lines.length}
            title={t('inspection.exportAllTitle')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded border text-sm text-green-700 border-green-200 hover:bg-green-50 disabled:opacity-50 whitespace-nowrap"
          >
            <Download className="w-4 h-4" />
            {t('inspection.exportAll')}
          </button>
        </div>
        <dl className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <dt className="text-sm text-gray-500">{t('inspection.invoiceDate')}</dt>
            <dd className="font-medium">{formatDate(invoice.invoice_date)}</dd>
          </div>
          {invoice.period_start && invoice.period_end && (
            <div>
              <dt className="text-sm text-gray-500">{t('inspection.period')}</dt>
              <dd className="font-medium">
                {formatDate(invoice.period_start)} &ndash; {formatDate(invoice.period_end)}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-sm text-gray-500">{t('inspection.netAmount')}</dt>
            <dd className="font-medium text-lg">
              {formatCurrency(invoice.net_amount)} &euro;
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">{t('inspection.tripCount')}</dt>
            <dd className="font-medium">{invoice.trip_count}</dd>
          </div>
          {invoice.billing_group && (
            <div>
              <dt className="text-sm text-gray-500">{t('inspection.billingGroup')}</dt>
              <dd className="font-medium">{invoice.billing_group}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Invoice Lines */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">{t('inspection.lines')}</h2>

        {invoice.lines.map((line) => (
          <div key={line.id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {/* Line header row */}
            <div className="px-6 py-3 bg-gray-50 border-b grid grid-cols-6 gap-4 items-center text-sm">
              <div className="font-medium">
                {t('inspection.lineNumber')} {line.line_number}
              </div>
              <div className="col-span-2">{line.description || ''}</div>
              <div>
                {line.cost_center_code && (
                  <span className="text-gray-600">
                    {line.cost_center_code}
                    {line.cost_center_name && ` ${line.cost_center_name}`}
                  </span>
                )}
              </div>
              <div className="text-right font-medium">
                {formatCurrency(line.amount_net)} &euro;
              </div>
              <div className="text-right text-gray-500">
                {line.trip_count ? `${line.trip_count} ${t('inspection.orderCount')}` : (
                  line.row_type === 'credit' ? t('inspection.lineCredit')
                  : line.row_type === 'correction' ? t('inspection.lineCorrection')
                  : line.row_type
                )}
              </div>
            </div>

            {/* Trip details table or access denied message */}
            {['trip', 'credit', 'correction'].includes(line.row_type || '') && line.has_inspection_access && line.trip_details.length > 0 && (
              <div className="p-2">
                <VirtualTable<TripDetail>
                  data={line.trip_details}
                  columns={tripDetailColumns}
                  estimateSize={28}
                  overscan={10}
                  rowCountLabel={t('inspection.rows')}
                  clearFiltersLabel={tc('common.clearFilters', { defaultValue: 'Tyhjennä' })}
                  maxHeight="60vh"
                  renderToolbarExtra={(filteredParents, _children, filterInfo) => (
                    <button
                      onClick={() => handleExportLine(line, filteredParents, filterInfo)}
                      disabled={!filteredParents.length}
                      className="flex items-center gap-1 px-2 py-1 rounded border hover:bg-green-50 text-green-700 border-green-200 disabled:opacity-50"
                    >
                      <Download className="w-3 h-3" />
                      {t('inspection.exportExcel')}
                    </button>
                  )}
                  renderExtraStats={(filteredParents) =>
                    renderInspectionStats(filteredParents)
                  }
                />
              </div>
            )}

            {['trip', 'credit', 'correction'].includes(line.row_type || '') && !line.has_inspection_access && (
              <div className="px-6 py-4 bg-amber-50 flex items-center gap-2 text-amber-700 text-sm">
                <Lock className="w-4 h-4" />
                {t('inspection.noAccess')}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
